import React, { useMemo } from "react";
import {
  Grid,
  TextField,
  Typography,
  Button,
  Divider,
  useMediaQuery,
  useTheme,
  IconButton,
  Box,
  Autocomplete,
  Chip,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

/** Helpers to compute cost/gram so we can show informative labels */
const n = (v, f = 0) => {
  const x = Number(v);
  return Number.isFinite(x) ? x : f;
};

function computeBatchCost(profile) {
  const ingCost = (profile.ingredients || []).reduce(
    (sum, i) => sum + n(i.quantity) * n(i.costPerUnitSnapshot),
    0
  );
  const pkgCost = profile.includePackaging
    ? (profile.packaging || []).reduce((s, p) => s + n(p.unitCost), 0)
    : 0;
  const laborCost = profile.includeLabor
    ? (n(profile.laborMinutes) / 60) * n(profile.laborRate)
    : 0;
  const adjCost = (profile.manualAdjustments || []).reduce(
    (s, a) => s + n(a.amount),
    0
  );
  return ingCost + pkgCost + laborCost + adjCost;
}

function computeCostPerGram(profile) {
  const yieldG =
    (profile.componentYieldUnit || "g").toLowerCase() === "kg"
      ? n(profile.componentYield) * 1000
      : n(profile.componentYield);
  if (!yieldG) return null;
  const batchCost = computeBatchCost(profile);
  return batchCost / yieldG; // $ / g
}

function formatComponentLabel(c) {
  const name =
    c.name ||
    c.productId?.displayName ||
    c.productId?.name ||
    (c.type ? `${c.type}` : c._id?.slice(-6) || "Component");

  const parts = [name];

  if (c.componentYield) {
    parts.push(`${n(c.componentYield)} ${c.componentYieldUnit || "g"}`);
  }

  const cpg = computeCostPerGram(c);
  if (cpg != null && isFinite(cpg)) {
    parts.push(`$${(cpg * 100).toFixed(2)}/100 g`);
  }

  return parts.join(" · ");
}

const ComponentSection = ({ form, updateField, componentProfiles }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Make sure we only get true components and sort by nice label
  const options = useMemo(() => {
    const list = Array.isArray(componentProfiles) ? componentProfiles : [];
    return list
      .filter((c) => c?.isComponent)
      .map((c) => ({ ...c, __label: formatComponentLabel(c) }))
      .sort((a, b) => a.__label.localeCompare(b.__label));
  }, [componentProfiles]);

  const components = Array.isArray(form.components) ? form.components : [];

  const handleComponentChange = (index, key, value) => {
    const updated = [...components];
    updated[index] = { ...updated[index], [key]: value };
    updateField("components", updated);
  };

  const addComponent = () => {
    updateField("components", [
      ...components,
      { componentId: "", usedGrams: 0 },
    ]);
  };

  const removeComponent = (index) => {
    const updated = [...components];
    updated.splice(index, 1);
    updateField("components", updated);
  };

  return (
    <Grid item xs={12}>
      <Divider sx={{ mt: 4, mb: 2 }} />
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Reusable Components (e.g., Buttercream)
      </Typography>

      {components.map((comp, idx) => {
        const selected =
          options.find((o) => o._id === comp.componentId) || null;
        const selectedCostPerGram = selected
          ? computeCostPerGram(selected)
          : null;

        return (
          <Grid
            container
            spacing={1}
            alignItems="center"
            key={idx}
            sx={{ mb: 2, px: { xs: 1, md: 0 } }}>
            <Grid item xs={12} md={7}>
              <Autocomplete
                options={options}
                getOptionLabel={(opt) => opt.__label || ""}
                value={selected}
                onChange={(_, val) =>
                  handleComponentChange(idx, "componentId", val?._id || "")
                }
                renderInput={(params) => (
                  <TextField {...params} label="Component" fullWidth />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option._id}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <span>{option.__label}</span>
                    </Box>
                  </li>
                )}
                isOptionEqualToValue={(a, b) => a._id === b._id}
              />
            </Grid>

            <Grid item xs={8} md={3}>
              <TextField
                type="number"
                label="Used (g)"
                fullWidth
                value={comp.usedGrams}
                onChange={(e) =>
                  handleComponentChange(
                    idx,
                    "usedGrams",
                    Number(e.target.value)
                  )
                }
                inputProps={{ min: 0, step: "1" }}
                helperText={
                  selectedCostPerGram != null
                    ? `≈ $${(
                        Number(comp.usedGrams || 0) * selectedCostPerGram
                      ).toFixed(2)} for this component`
                    : ""
                }
              />
            </Grid>

            <Grid item xs={4} md={2}>
              <IconButton
                onClick={() => removeComponent(idx)}
                color="error"
                sx={{ mt: { xs: 2, md: 0 } }}
                aria-label="Remove component">
                <DeleteOutlineIcon fontSize="medium" />
              </IconButton>
            </Grid>
          </Grid>
        );
      })}

      <Button
        onClick={addComponent}
        variant="contained"
        fullWidth={isMobile}
        sx={{ mt: 1 }}>
        + Add Component
      </Button>
    </Grid>
  );
};

export default ComponentSection;
